import type { User, UserRole } from '@/entities/user/model'
import { api } from '@/shared/api/http'

/** `POST /auth/login` and `POST /auth/refresh` response (SPEC.md §9.2). */
export interface TokenResponse {
  access_token: string
  token_type: string
  expires_in: number
  must_change_password: boolean
  user: User
}

/** `GET /auth/me` — the current user plus the permissions the UI gates on. */
export interface MeResponse {
  user: User
  role: UserRole
  permissions: string[]
}

export interface SessionItem {
  id: string
  user_agent: string | null
  ip_address: string | null
  created_at: string
  expires_at: string
  is_current: boolean
}

export interface LoginPayload {
  username: string
  password: string
  remember_me: boolean
}

// `withCredentials` on the auth routes only: they are the only endpoints that read or write the
// refresh cookie, and sending cookies everywhere would widen the CSRF surface for no reason.
const AUTH_OPTIONS = { withCredentials: true } as const

export function login(payload: LoginPayload): Promise<TokenResponse> {
  return api.post<TokenResponse>('/auth/login', payload, AUTH_OPTIONS)
}

export function logout(): Promise<{ sessions_revoked: number }> {
  return api.post<{ sessions_revoked: number }>('/auth/logout', undefined, AUTH_OPTIONS)
}

export function logoutAll(): Promise<{ sessions_revoked: number }> {
  return api.post<{ sessions_revoked: number }>('/auth/logout-all', undefined, AUTH_OPTIONS)
}

export function fetchMe(): Promise<MeResponse> {
  return api.get<MeResponse>('/auth/me')
}

export function changePassword(payload: {
  current_password: string
  new_password: string
}): Promise<{ sessions_revoked: number }> {
  return api.patch<{ sessions_revoked: number }>('/auth/me/password', payload, AUTH_OPTIONS)
}

export function fetchSessions(): Promise<SessionItem[]> {
  return api.get<SessionItem[]>('/auth/sessions')
}

export function revokeSession(sessionId: string): Promise<void> {
  return api.delete<void>(`/auth/sessions/${sessionId}`)
}
